import './Vendors.css';
import { useState } from 'react';

function Vendors() {
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    repName: '',
    businessName: '',
    address: '',
    phoneNumber: '',
    email: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const [errors, setErrors] = useState({});

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhone = (phone) => {
    // Remove all non-digits
    const digitsOnly = phone.replace(/\D/g, '');
    // Check if it's 10 or 11 digits (with or without country code)
    return digitsOnly.length >= 10 && digitsOnly.length <= 11;
  };

  const formatPhone = (value) => {
    // Remove all non-digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Format as (XXX) XXX-XXXX
    if (digitsOnly.length >= 6) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3, 6)}-${digitsOnly.slice(6, 10)}`;
    } else if (digitsOnly.length >= 3) {
      return `(${digitsOnly.slice(0, 3)}) ${digitsOnly.slice(3)}`;
    } else {
      return digitsOnly;
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;
    let newErrors = { ...errors };

    // Format phone number as user types
    if (name === 'phoneNumber') {
      processedValue = formatPhone(value);
      
      // Validate phone number
      if (value && !validatePhone(value)) {
        newErrors.phoneNumber = 'Please enter a valid phone number';
      } else {
        delete newErrors.phoneNumber;
      }
    }

    // Validate email
    if (name === 'email') {
      if (value && !validateEmail(value)) {
        newErrors.email = 'Please enter a valid email address';
      } else {
        delete newErrors.email;
      }
    }

    // Clear other field errors when user starts typing
    if (value.trim() !== '' && newErrors[name] && name !== 'phoneNumber' && name !== 'email') {
      delete newErrors[name];
    }

    setErrors(newErrors);
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Check required fields
    if (!formData.repName.trim()) {
      newErrors.repName = 'Representative name is required';
    }
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData.address.trim()) {
      newErrors.address = 'Business address is required';
    }
    
    if (!formData.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    } else if (!validatePhone(formData.phoneNumber)) {
      newErrors.phoneNumber = 'Please enter a valid phone number';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form before submitting
    const formErrors = validateForm();
    
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      setSubmitMessage('Please correct the errors above before submitting.');
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    // Create mailto link
    const subject = encodeURIComponent('Vendor inquiry');
    const body = encodeURIComponent(`
      New Vendor Partnership Inquiry

      Representative Name: ${formData.repName}
      Business Name: ${formData.businessName}
      Address: ${formData.address}
      Phone Number: ${formData.phoneNumber}
      Email: ${formData.email}

      Please contact this business about partnering with TAP.
    `);
    
    const mailtoLink = `mailto:support@smallrobot.app?subject=${subject}&body=${body}`;
    
    // Open email client
    window.location.href = mailtoLink;
    
    // Show success message
    setSubmitMessage('Thank you! Your inquiry has been sent. We\'ll be in touch soon.');
    setIsSubmitting(false);
    
    // Reset form after delay
    setTimeout(() => {
      setFormData({
        repName: '',
        businessName: '',
        address: '',
        phoneNumber: '',
        email: ''
      });
      setShowForm(false);
      setSubmitMessage('');
      setErrors({});
    }, 3000);
  };

  return (
    <div className="vendors">
      <section className="apps-hero">
        <div className="apps-hero-img">
          <img src="/assets/hero_img_apps.png" alt="Apps Hero Image" />
        </div>
        <div className="container">
          <div className="header">
            <h1>Partner with us</h1>
          </div>
          <p>Local businesses are the backbone of our community, and we can all grow together.</p>
        </div>
      </section>

      <section className="apps-content">
        <div className="container">
          <div className="header">
            <h2>TAP - For Business</h2>
            <p>With our partner portal, you can create TAP Deals to attract new customers. You will also be given table top signs TAP users can ask for to signal they want to meet new people.
              TAP partners are instrumental in providing space for TAP users to meet new people.
            </p>
            {!showForm ? (
              <div className="buttons">
                <button className="cta-primary" onClick={() => setShowForm(true)}>
                  Get Started
                </button>
              </div>
            ) : (
              <div className="contact-form-section">
                <h3>Partner Inquiry Form</h3>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="repName">Representative Name *</label>
                    <input
                      type="text"
                      id="repName"
                      name="repName"
                      value={formData.repName}
                      onChange={handleInputChange}
                      className={errors.repName ? 'error' : ''}
                      required
                    />
                    {errors.repName && <span className="error-message">{errors.repName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="businessName">Business Name *</label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleInputChange}
                      className={errors.businessName ? 'error' : ''}
                      required
                    />
                    {errors.businessName && <span className="error-message">{errors.businessName}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="address">Business Address *</label>
                    <textarea
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      rows="3"
                      className={errors.address ? 'error' : ''}
                      required
                    />
                    {errors.address && <span className="error-message">{errors.address}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="phoneNumber">Phone Number *</label>
                    <input
                      type="tel"
                      id="phoneNumber"
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder="(555) 123-4567"
                      className={errors.phoneNumber ? 'error' : ''}
                      required
                    />
                    {errors.phoneNumber && <span className="error-message">{errors.phoneNumber}</span>}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="name@business.com"
                      className={errors.email ? 'error' : ''}
                      required
                    />
                    {errors.email && <span className="error-message">{errors.email}</span>}
                  </div>
                  
                  <div className="form-buttons">
                    <button type="submit" className="cta-primary" disabled={isSubmitting}>
                      {isSubmitting ? 'Sending...' : 'Send Inquiry'}
                    </button>
                    <button type="button" className="cta-secondary" onClick={() => setShowForm(false)}>
                      Cancel
                    </button>
                  </div>
                  
                  {submitMessage && (
                    <div className={`submit-message ${submitMessage.includes('correct the errors') ? 'error' : 'success'}`}>
                      {submitMessage}
                    </div>
                  )}
                </form>
              </div>
            )}
          </div>
          <div className="app-card">
            <div className="app-img">
              <img src="/assets/tap_biz_screen.png" alt="TAP for business" />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Vendors;